import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import React, { useState, useEffect, useId, useContext, ChangeEvent } from 'react';
import cloneDeep from 'lodash.clonedeep';

import {
  useLearningJourneyDiscussionTeamsQueryRemote,
  useBootstrapLearningJourneySessionDiscussionTeamsMutationRemote,
  RLearningJourneySessionDiscussionTeam,
  RLearningJourneySessionDiscussionTeamMember,
} from '@/store/v2';
import { Button } from '@/components/Button/Button';
import { BUTTON_HEIGHT_ENUM } from '@/components/Button/components/BaseButton';
import { cvar } from '@/styles/GlobalStyle';
import { JourneyEditViewTeamsList } from '@/features/journey/admin/molecules/JourneyEditViewTeamsList';
import { Loader } from '@/component/Loader';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import {
  getJourneyTeamsCsvImportStartedMessage,
  JOURNEY_TEAMS_CSV_IMPORT_CONFIRM_ERROR_MESSAGE,
} from '@/features/csvImport/admin/constants/journeyTeamsCsvImport';

const UncreatedTeamsStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 380px;
  padding-left: 24px;
  padding-right: 24px;
`;

const UncreatedSubtitleStyled = styled.div`
  ${({ theme }) => theme.typography.body2};
  color: ${(p) => p.theme.colors.neutral.outline2};
  margin-bottom: 4px;
`;

const UncreatedDescriptionStyled = styled.div`
  ${({ theme }) => theme.typography.caption};
  color: ${(p) => p.theme.colors.neutral.outline2};
  margin-bottom: 24px;
`;

/**
 * The field label used to be a `Button` with the static styling, which made it a focusable control
 * that did nothing and could not name the select. It keeps that look, with the height taken from the
 * button scale so it still lines up with Set up beside it.
 */
const TeamSizeLabelStyled = styled.label`
  align-items: center;
  background-color: ${cvar('color-secondary')};
  border: ${cvar('border-width')} solid ${cvar('color-secondary')};
  border-radius: ${cvar('radius-inner')};
  color: ${cvar('color-black')};
  display: inline-flex;
  font-size: ${cvar('font-size-8')};
  font-weight: ${cvar('font-weight-bold-extra')};
  height: ${BUTTON_HEIGHT_ENUM.default}rem;
  justify-content: center;
  letter-spacing: 0.2px;
  padding: 0 1.375rem;
  text-transform: uppercase;
`;

/** The snackbar drops a plain `\n`, so the messages carry their line break in the markup. */
const SnackbarMessageStyled = styled.span`
  white-space: pre-line;
`;

type JourneyEditViewTeamsContentProps = {
  teams: RLearningJourneySessionDiscussionTeam[];
  setTeams: (teams: RLearningJourneySessionDiscussionTeam[]) => void;
  setSessionId: (id: number | null) => void;
  /** Raised when the admin moves a member, which is the only thing Save has to write. */
  onTeamsReordered: VoidFunction;
};

export const JourneyEditViewTeamsContent = ({
  teams,
  setTeams,
  setSessionId,
  onTeamsReordered,
}: JourneyEditViewTeamsContentProps): React.ReactElement => {
  // Only drives the bootstrap step's size picker; once teams exist each carries its own size.
  const [teamsSize, setTeamsSize] = useState<number>(1);
  const teamSizeId = useId();
  const { journeyId } = useParams<{ journeyId: string }>();
  const { openSnackbar } = useContext(SharedSnackbarContext);

  const [bootstrapTeams] = useBootstrapLearningJourneySessionDiscussionTeamsMutationRemote();
  const { data, loading, refetch } = useLearningJourneyDiscussionTeamsQueryRemote({
    variables: { id: Number(journeyId) },
    skip: !journeyId,
  });

  useEffect(() => {
    const loadedTeams = data?.learningJourney?.sessions?.[0]?.discussionTeams as RLearningJourneySessionDiscussionTeam[];
    setTeams(loadedTeams);
    setSessionId(data?.learningJourney?.sessions?.[0]?.id as number);
  }, [data]);

  const areDiscussionTeamsEstablished = !!data?.learningJourney?.sessions?.[0]?.discussionTeams?.length;

  const handleTeamSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setTeamsSize(Number(event.target.value));
  };

  // Matches the CSV set-up: errors stand until the admin acts on them, success goes down on its own.
  const showMessage = (type: SnackbarType, message: string) =>
    openSnackbar({
      type,
      message: <SnackbarMessageStyled>{message}</SnackbarMessageStyled>,
      isDismissive: type === SnackbarType.SUCCESS,
    });

  /**
   * Reports the outcome in the same words as the CSV set-up, so both ways of populating a cohort
   * read alike. Bootstrap answers with a bare `success`, so the team count comes from the teams
   * the refetch brings back.
   */
  const handleSettingUpTeams = async () => {
    const { data: result } = await bootstrapTeams({
      variables: { teamSize: Number(teamsSize), sessionId: data?.learningJourney?.sessions?.[0]?.id as number },
      update(cache, { data }) {
        if (data?.bootstrapLearningJourneySesssionDiscussionTeams?.success) {
          cache.evict({ fieldName: 'learningJourney' });
        }
      },
    });

    if (!result?.bootstrapLearningJourneySesssionDiscussionTeams?.success) {
      showMessage(SnackbarType.DANGER, JOURNEY_TEAMS_CSV_IMPORT_CONFIRM_ERROR_MESSAGE);

      return;
    }

    const { data: refetched } = await refetch();
    const teamsCreated = refetched?.learningJourney?.sessions?.[0]?.discussionTeams?.length ?? 0;

    showMessage(SnackbarType.SUCCESS, getJourneyTeamsCsvImportStartedMessage(teamsCreated));
  };

  const handleTeamSwitch = ({ newTeamId, oldTeamId, memberId }: { newTeamId: number; oldTeamId: number; memberId: number }) => {
    const newTeams = cloneDeep(teams);
    let movedMember: RLearningJourneySessionDiscussionTeamMember;

    newTeams?.find((team, index) => {
      if (team.id === oldTeamId) {
        newTeams[index].members = newTeams[index].members?.filter((member) => {
          if (member?.user?.id === memberId) {
            movedMember = cloneDeep(member);
          }

          return member?.user?.id !== memberId;
        });

        return true;
      }
    });

    newTeams?.find((team, index) => {
      if (team.id === newTeamId) {
        newTeams[index].members.push(movedMember);
        return true;
      }
    });

    setTeams(newTeams);
    onTeamsReordered();
  };

  if (loading) return <Loader />;

  if (!areDiscussionTeamsEstablished) {
    return (
      <UncreatedTeamsStyled>
        <UncreatedSubtitleStyled>Learning teams setup</UncreatedSubtitleStyled>
        <UncreatedDescriptionStyled>
          Learning teams are generated based on available learners and aim for a balanced distribution across user realms. The
          team size you select will stay fixed unless you reset the teams and start over.
        </UncreatedDescriptionStyled>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <div className="field has-addons">
            <div className="control">
              <TeamSizeLabelStyled htmlFor={teamSizeId}>Pick team size</TeamSizeLabelStyled>
            </div>
            <div className="control">
              <div className="select">
                <select className="select" id={teamSizeId} value={teamsSize} onChange={handleTeamSizeChange}>
                  {Array.from({ length: 10 }, (_v, index) => (
                    <option key={index} value={index + 1}>
                      {index + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="field">
            <Button onClick={handleSettingUpTeams}>Set up</Button>
          </div>
        </div>
      </UncreatedTeamsStyled>
    );
  }

  if (loading || !teams) return <div />;

  return <JourneyEditViewTeamsList teams={teams} onTeamSwitch={handleTeamSwitch} />;
};
